/**
 * Dialog
 * ──────
 * The one modal. Rendered in a portal on the modal z layer, labelled for
 * screen readers, traps Tab inside itself, closes on Escape and on backdrop
 * click, and gives focus back to whatever opened it. Reduced-motion users
 * get no scale animation.
 *
 *   <Dialog open={open} onClose={close} title="Rename widget" size="sm"
 *           footer={<><Button variant="secondary" onClick={close}>Cancel</Button>
 *                    <Button onClick={save}>Save</Button></>}>
 *     <Input … />
 *   </Dialog>
 */
import React, { useEffect, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import { LuX } from 'react-icons/lu';

const SIZES = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

let openCount = 0;

const Dialog = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  showClose = true,
  initialFocusRef,
  className = '',
  bodyClassName = '',
}) => {
  const panelRef = useRef(null);
  const restoreRef = useRef(null);
  // Callers almost always pass an inline `onClose`, which is a new function
  // on every render. If the effect below depended on it, every keystroke in
  // a field would re-run it: the cleanup hands focus back to the opener and
  // the next frame focuses the first field, so typing lost the caret after
  // one character. Read the latest callback through a ref instead.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = useId();
  const descId = useId();

  // Focus management + Escape + scroll lock.
  useEffect(() => {
    if (!open) return undefined;
    restoreRef.current = document.activeElement;
    openCount += 1;
    document.body.style.overflow = 'hidden';

    const focusFirst = () => {
      const target =
        initialFocusRef?.current ||
        panelRef.current?.querySelector('[data-autofocus]') ||
        panelRef.current?.querySelector(FOCUSABLE) ||
        panelRef.current;
      target?.focus?.();
    };
    const raf = requestAnimationFrame(focusFirst);

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
      if (nodes.length === 0) { e.preventDefault(); panelRef.current.focus(); return; }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey, true);
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) document.body.style.overflow = '';
      restoreRef.current?.focus?.();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  return createPortal(
    <div
      className="cx-dialog-root fixed inset-0 z-[var(--cx-z-modal)] flex items-center justify-center p-4 sm:p-6"
      onMouseDown={(e) => { if (closeOnBackdrop && e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="cx-dialog-backdrop absolute inset-0 bg-gray-900/40" aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`cx-dialog-panel relative w-full ${SIZES[size] || SIZES.md} max-h-[calc(100vh-2rem)] flex flex-col bg-white rounded-xl shadow-lg border border-line outline-none ${className}`}
      >
        {(title || showClose) && (
          <div className="flex items-start gap-4 px-6 pt-6 pb-2">
            <div className="min-w-0 flex-1">
              {title && <h2 id={titleId} className="text-[18px] font-bold text-ink m-0 leading-7">{title}</h2>}
              {description && <p id={descId} className="text-sm text-text-2 m-0 mt-1 leading-relaxed">{description}</p>}
            </div>
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 -m-2 p-2 rounded-lg text-text-3 hover:bg-canvas-3 hover:text-ink transition-colors"
                aria-label="Close"
              >
                <LuX className="w-5 h-5" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        <div className={`px-6 py-4 overflow-y-auto min-h-0 ${bodyClassName}`}>{children}</div>
        {footer && <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-2">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Dialog;
